import { Component, OnInit, WritableSignal, inject, signal } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose,
} from '@angular/material/dialog';
import { Organization } from '../../organization/organization-models';
import { PageEvent } from '@angular/material/paginator';
import { map, mergeMap, Observable, of } from 'rxjs';
import { DepartmentService } from 'src/app/shared/services/department.service';
import { Department, DepartmentSearchEvent } from '../../department/department.model';
import { ChargingUserService, SwitchChargingUserOrganizationRequest } from 'src/app/shared/services/charging-user.service';
import { HttpStatusCode } from '@angular/common/http';
import { SnackbarService, FeedbackInfoComponent } from 'ok-angular-shared/components';
import { ErrorMessage } from 'src/app/shared/models/errorMessage.model';
import { PagedResult } from 'src/app/shared/models/paging';
import { AccessGroup, AccessGroupSearchEvent } from '../../access-groups/access-group.model';
import { AccessGroupService } from 'src/app/shared/services/access-group.service';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { AdmOrgTypeaheadComponent } from 'src/app/shared/components/adm-org-typeahead/adm-org-typeahead.component';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { AsyncPipe } from '@angular/common';

export interface ISwitchOrganizationForm {
  departmentId: FormControl<number | null>;
  organization: FormControl<Organization | null>;
  selectedAccessGroups: FormControl<SelectedAccessGroup[]>;
}
interface SelectedAccessGroup {
  identifier: string;
  name: string;
}
export interface ISwitchOrganizationModalData {
  organizationId: number;
  departmentId: number;
  chargingUserId: number;
}

@Component({
  selector: 'adm-switch-organization-modal',
  templateUrl: './switch-organization-modal.component.html',
  styleUrl: './switch-organization-modal.component.scss',
  imports: [
    AdmOrgTypeaheadComponent,
    CustomDropDownArrowDirective,
    FeedbackInfoComponent,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatFormField,
    MatLabel,
    MatSelect,
    MatSelectTrigger,
    MatOption,
    MatIcon,
    MatButton,
    ReactiveFormsModule,
    AsyncPipe,
  ],
})
export class SwitchOrganizationModalComponent implements OnInit {
  private departmentService = inject(DepartmentService);
  dialogRef = inject<MatDialogRef<SwitchOrganizationModalComponent>>(MatDialogRef);
  data = inject<ISwitchOrganizationModalData>(MAT_DIALOG_DATA);
  userService = inject(ChargingUserService);
  private snackbarService = inject(SnackbarService);
  private accessGroupService = inject(AccessGroupService);

  form: FormGroup<ISwitchOrganizationForm> = new FormGroup<ISwitchOrganizationForm>({
    departmentId: new FormControl<number | null>(0, Validators.required),
    organization: new FormControl<Organization | null>(null, [Validators.required]),
    selectedAccessGroups: new FormControl<SelectedAccessGroup[]>([], { nonNullable: true }),
  });
  resetOrgTrigger: WritableSignal<{ reset: boolean }> = signal<{ reset: boolean }>({ reset: false });
  departments$: Observable<Array<Department>>;
  accessGroups$: Observable<Array<AccessGroup>>;

  ngOnInit(): void {
    this.form.patchValue({
      departmentId: this.data.departmentId,
    });

    this.departments$ = this.form.controls.organization.valueChanges.pipe(
      mergeMap(x => {
        if (x == null) {
          return of<PagedResult<Department>>({ items: [], totalCount: 0, pageIndex: 0, pageSize: 0 });
        }

        return this.departmentService.getDepartments(<DepartmentSearchEvent>{
          paging: <PageEvent>{
            pageIndex: 0,
            pageSize: 1000,
          },
          organizationId: x.id,
        });
      }),
      map(x => {
        return x.items;
      })
    );

    this.accessGroups$ = this.form.controls.organization.valueChanges.pipe(
      mergeMap(x => {
        if (x == null) {
          return of<PagedResult<AccessGroup>>({ items: [], totalCount: 0, pageIndex: 0, pageSize: 0 });
        }

        return this.accessGroupService.getAccessGroups(<AccessGroupSearchEvent>{
          paging: <PageEvent>{
            pageIndex: 0,
            pageSize: 1000,
          },
          organizationId: x.id,
        });
      }),
      map(x => {
        return x.items;
      })
    );
  }

  resetOrganization() {
    this.form.controls.organization.setValue(null);
    this.form.controls.departmentId.setValue(null);
  }
  organizationSelected(org: Organization | null) {
    this.form.patchValue({
      organization: org,
      departmentId: null,
      selectedAccessGroups: [],
    });
  }

  close() {
    this.dialogRef.close();
  }

  submit() {
    const organization = this.form.controls.organization.value;
    const departmentId = this.form.controls.departmentId.value;
    if (!organization || departmentId == null) {
      return;
    }

    this.userService
      .changeOrganization(this.data.chargingUserId, <SwitchChargingUserOrganizationRequest>{
        departmentId,
        organizationId: organization.id,
        accessGroupIdentifiers: this.form.controls.selectedAccessGroups.value.map(x => x.identifier),
      })
      .subscribe({
        next: resp => {
          this.dialogRef.close(resp);
        },
        error: httpError => {
          if (httpError.status == HttpStatusCode.BadRequest && httpError.error) {
            const error = httpError.error as ErrorMessage;
            if (error.code === 600002) {
              this.snackbarService.warning(`Bruger er ikke fundet (${error.message})`);
            }
            if (error.code === 500016) {
              this.snackbarService.warning(`Bruger kan ikke skifte til samme organisation (${error.message})`);
            }
            if (error.code === 500017) {
              this.snackbarService.warning(`Organisation mangler Id Tag gruppe (${error.message})`);
            }
          }
        },
      });
  }
}
